import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Image, Trash2, Upload, Search, X, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { api } from '@/api';
import { resolveMediaUrl } from '@/utils/helpers';

const PAGE_SIZE = 60;

// Debounce helper
const useDebounced = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const AdminMediaLibrary = () => {
  const [media, setMedia] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const search = useDebounced(searchInput.trim(), 300);
  const tag = useDebounced(filterTag.trim(), 300);

  // Pagination
  const [page, setPage] = useState(0); // zero-based

  // ====== Multi-select bulk actions ======
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const allSelected = media.length > 0 && media.every(m => selectedIds.has(m.id));
  const someSelected = selectedIds.size > 0 && !allSelected;
  const toggleOne = (id) => setSelectedIds(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });
  const togglePage = () => {
    setSelectedIds(prev => {
      if (allSelected) {
        const next = new Set(prev);
        media.forEach(m => next.delete(m.id));
        return next;
      }
      const next = new Set(prev);
      media.forEach(m => next.add(m.id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const runBulk = async (kind) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      if (kind === 'delete') {
        if (!window.confirm(`Permanently delete ${ids.length} file${ids.length === 1 ? '' : 's'}? This cannot be undone — any team/game still referencing these images will lose them.`)) return;
        const res = await api.bulkDeleteMedia(ids);
        alert(`${res.data.deleted} file(s) deleted.`);
      } else if (kind === 'tag') {
        const tag = window.prompt(`Add a tag to ${ids.length} file(s).\nExisting tags will be kept.\n\nTag name:`);
        if (tag === null) return;
        const trimmed = tag.trim();
        if (!trimmed) { alert('Tag cannot be empty.'); return; }
        const res = await api.bulkAddTagMedia(ids, trimmed);
        alert(`Added tag "${res.data.tag}" to ${res.data.modified} file(s).`);
      }
      clearSelection();
      loadMedia();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Unknown error';
      alert(`Bulk action failed: ${detail}`);
      console.error(err);
    } finally {
      setBulkBusy(false);
    }
  };

  // Folder-upload <input> needs `webkitdirectory` set imperatively to avoid React
  // warnings about non-standard DOM attributes.
  const folderInputRef = useRef(null);
  useEffect(() => {
    if (folderInputRef.current) {
      folderInputRef.current.setAttribute('webkitdirectory', '');
      folderInputRef.current.setAttribute('directory', '');
      folderInputRef.current.setAttribute('mozdirectory', '');
    }
  }, []);

  const loadMedia = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getMedia(tag || null, {
        search: search || undefined,
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      setMedia(res.data);
      const t = parseInt(res.headers?.['x-total-count'] ?? res.data.length, 10);
      setTotal(Number.isFinite(t) ? t : res.data.length);
    } catch (error) {
      console.error('Error loading media:', error);
    } finally {
      setLoading(false);
    }
  }, [tag, search, page]);

  // Reset to page 0 whenever filters change
  useEffect(() => { setPage(0); }, [search, tag]);

  useEffect(() => { loadMedia(); }, [loadMedia]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    setUploading(true);
    let succeeded = 0;
    let failed = 0;
    const failures = [];
    try {
      for (const file of files) {
        // Skip hidden / non-image files when uploading a whole folder (e.g. .DS_Store, Thumbs.db)
        if (!file.type.startsWith('image/')) continue;
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('alt_text', file.name);
          formData.append('tags', '');
          await api.uploadMedia(formData);
          succeeded += 1;
        } catch (err) {
          failed += 1;
          failures.push(file.name);
          console.error('Upload failed for', file.name, err);
        }
      }
      // Jump to first page so newly uploaded images are visible
      setPage(0);
      loadMedia();
      if (failed > 0) {
        alert(`Uploaded ${succeeded} file(s). ${failed} failed:\n${failures.slice(0, 10).join('\n')}${failures.length > 10 ? `\n…and ${failures.length - 10} more` : ''}`);
      }
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await api.deleteMedia(id);
      loadMedia();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Error deleting media: ${detail}`);
      console.error('Delete media failed:', error);
    }
  };

  // "Replace" reuses the same S3 key so the public URL stays stable —
  // every game/team/OG-tag reference to this asset keeps working.
  const [replacingId, setReplacingId] = useState(null);
  const handleReplace = async (mediaId, file) => {
    if (!file) return;
    if (!window.confirm(
      `Replace this file with "${file.name}"? The URL will stay the same so existing references keep working.`
    )) return;
    setReplacingId(mediaId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.replaceMedia(mediaId, fd);
      loadMedia();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Replace failed: ${detail}`);
      console.error('Replace media failed:', error);
    } finally {
      setReplacingId(null);
    }
  };

  const copyUrl = (url) => {
    const fullUrl = resolveMediaUrl(url);
    navigator.clipboard.writeText(fullUrl);
    alert('URL copied to clipboard!');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasPrev = page > 0;
  const hasNext = page + 1 < pageCount;
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, (page + 1) * PAGE_SIZE);
  const filtered = Boolean(search || tag);

  return (
    <div className="space-y-6" data-testid="admin-media-library">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <h2 className="text-2xl font-bold text-white">
          <Image className="w-6 h-6 inline mr-2" />
          Media Library{' '}
          <span className="text-sky-400 text-base font-normal" data-testid="media-total-count">
            ({total.toLocaleString()})
          </span>
        </h2>
        <div className="flex flex-wrap gap-2 items-center">
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              placeholder="Search filename or alt text..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="bg-gray-700 border-gray-600 text-white w-64 pl-9 pr-8"
              data-testid="media-search-input"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                aria-label="Clear search"
                data-testid="media-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {/* Tag filter */}
          <Input
            placeholder="Filter by tag..."
            value={filterTag}
            onChange={(e) => setFilterTag(e.target.value)}
            className="bg-gray-700 border-gray-600 text-white w-40"
            data-testid="media-tag-input"
          />
          {/* Upload Files */}
          <label className="cursor-pointer">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
              data-testid="media-upload-input"
            />
            <Button className="bg-sky-400 hover:bg-sky-500 text-gray-900" disabled={uploading} asChild>
              <span><Upload className="w-4 h-4 mr-2" /> {uploading ? 'Uploading...' : 'Upload Images'}</span>
            </Button>
          </label>
          {/* Upload Folder — pick an entire directory of images at once */}
          <label className="cursor-pointer">
            <input
              ref={folderInputRef}
              type="file"
              multiple
              onChange={handleUpload}
              className="hidden"
              data-testid="media-upload-folder-input"
            />
            <Button
              variant="outline"
              className="border-sky-400 text-sky-400 hover:bg-sky-400/10"
              disabled={uploading}
              asChild
              title="Upload an entire folder of images at once. Use the filenames in your CSV imports."
            >
              <span><Upload className="w-4 h-4 mr-2" /> {uploading ? 'Uploading...' : 'Upload Folder'}</span>
            </Button>
          </label>
        </div>
      </div>

      {/* Result summary + multi-select toolbar */}
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div className="text-sm text-gray-400" data-testid="media-result-summary">
          {total === 0
            ? (filtered ? 'No matches for current filters.' : 'No images uploaded yet.')
            : `Showing ${rangeStart}-${rangeEnd} of ${total.toLocaleString()}${filtered ? ' matching' : ''}`}
        </div>
        {filtered && (
          <button
            onClick={() => { setSearchInput(''); setFilterTag(''); }}
            className="text-sky-400 hover:text-sky-300 text-sm"
            data-testid="media-clear-filters"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Sticky multi-select toolbar */}
      {media.length > 0 && (
        <div
          className={`sticky top-0 z-10 -mx-2 px-3 py-2 rounded-lg border flex flex-wrap items-center justify-between gap-3 transition-colors ${
            selectedIds.size > 0
              ? 'bg-sky-500/20 border-sky-500/60'
              : 'bg-gray-800/60 border-gray-700'
          }`}
          data-testid="media-bulk-toolbar"
        >
          <label className="flex items-center gap-2 text-white text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={allSelected}
              ref={el => { if (el) el.indeterminate = someSelected; }}
              onChange={togglePage}
              className="w-4 h-4 accent-sky-400"
              data-testid="media-select-all"
            />
            <span className="font-medium">
              {selectedIds.size === 0
                ? `Select all ${media.length} on this page`
                : `${selectedIds.size} selected`}
            </span>
          </label>
          {selectedIds.size > 0 && (
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                className="border-sky-400/50 text-sky-300 hover:bg-sky-400/10"
                disabled={bulkBusy}
                onClick={() => runBulk('tag')}
                data-testid="media-bulk-tag"
                title="Add a tag to all selected files (existing tags are kept)"
              >
                Add Tag
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={bulkBusy}
                onClick={() => runBulk('delete')}
                data-testid="media-bulk-delete"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete {selectedIds.size}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-gray-300 hover:bg-gray-700"
                onClick={clearSelection}
                data-testid="media-bulk-clear"
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      )}

      <Card className="bg-gray-800/50 border-sky-900/30">
        <CardContent className="p-6">
          {loading && media.length === 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="aspect-square bg-gray-700/50 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : media.length === 0 ? (
            <div className="text-center py-12">
              <Image className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">
                {filtered ? 'No images match your filters' : 'No images uploaded yet'}
              </p>
              <p className="text-gray-500">
                {filtered
                  ? 'Try a different search term or clear the filters.'
                  : 'Upload images to use for backgrounds, logos, and thumbnails'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="media-grid">
              {media.map((item) => {
                const checked = selectedIds.has(item.id);
                return (
                <div
                  key={item.id}
                  className={`group relative aspect-square bg-gray-700 rounded-lg overflow-hidden cursor-pointer ${checked ? 'ring-2 ring-sky-400' : ''}`}
                  onClick={() => setSelectedMedia(item)}
                  data-testid={`media-item-${item.id}`}
                >
                  {/* Selection checkbox — top-left, always visible if checked, on hover otherwise */}
                  <label
                    className={`absolute top-2 left-2 z-10 bg-black/60 rounded p-1 transition-opacity ${checked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleOne(item.id)}
                      className="w-4 h-4 accent-sky-400 cursor-pointer block"
                      data-testid={`media-row-checkbox-${item.id}`}
                      aria-label={`Select ${item.original_name}`}
                    />
                  </label>
                  <img
                    src={resolveMediaUrl(item.url)}
                    alt={item.alt_text || item.original_name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5 flex-wrap p-2">
                    <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); copyUrl(item.url); }} data-testid={`media-copy-${item.id}`}>
                      Copy URL
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={replacingId === item.id}
                      onClick={(e) => { e.stopPropagation(); document.getElementById(`replace-${item.id}`)?.click(); }}
                      data-testid={`media-replace-${item.id}`}
                      title="Replace this file (URL stays the same)"
                    >
                      <RefreshCw className={`w-4 h-4 mr-1 ${replacingId === item.id ? 'animate-spin' : ''}`} />
                      {replacingId === item.id ? 'Replacing…' : 'Replace'}
                    </Button>
                    <input
                      id={`replace-${item.id}`}
                      type="file"
                      accept="image/*,video/*,audio/*"
                      className="hidden"
                      onChange={(e) => { handleReplace(item.id, e.target.files?.[0]); e.target.value = ''; }}
                    />
                    <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} data-testid={`media-delete-${item.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-2 py-1 text-xs text-white truncate">
                    {item.original_name}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="flex justify-between items-center" data-testid="media-pagination">
          <Button
            variant="outline"
            size="sm"
            disabled={!hasPrev}
            onClick={() => setPage(p => Math.max(0, p - 1))}
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
            data-testid="media-page-prev"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <div className="text-gray-400 text-sm" data-testid="media-page-indicator">
            Page {page + 1} of {pageCount}
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasNext}
            onClick={() => setPage(p => p + 1)}
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
            data-testid="media-page-next"
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Media Detail Modal */}
      <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
        <DialogContent className="max-w-2xl bg-gray-900 border-sky-900/50">
          <DialogHeader>
            <DialogTitle className="text-white">Image Details</DialogTitle>
          </DialogHeader>
          {selectedMedia && (
            <div className="space-y-4">
              <div className="aspect-video bg-gray-800 rounded-lg overflow-hidden">
                <img
                  src={resolveMediaUrl(selectedMedia.url)}
                  alt={selectedMedia.alt_text}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-gray-400">Filename</Label>
                  <p className="text-white">{selectedMedia.original_name}</p>
                </div>
                <div>
                  <Label className="text-gray-400">Size</Label>
                  <p className="text-white">{formatFileSize(selectedMedia.file_size)}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-400">URL</Label>
                  <div className="flex gap-2">
                    <Input
                      value={resolveMediaUrl(selectedMedia.url)}
                      readOnly
                      className="bg-gray-700 border-gray-600 text-white text-sm"
                    />
                    <Button size="sm" onClick={() => copyUrl(selectedMedia.url)}>Copy</Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { AdminMediaLibrary };
